import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import {
  UserCircleIcon,
  ChevronUpIcon,
  SunIcon,
  MoonIcon,
  KeyIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import FeedbackDialog from './FeedbackDialog';

interface UserMenuProps {
  isCollapsed: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isCollapsed }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check for dark mode on mount
  useEffect(() => {
    const darkMode = document.documentElement.classList.contains('dark');
    setIsDarkMode(darkMode);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (!user) return '?';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.email[0].toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'Benutzer';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        title={getUserDisplayName()}
      >
        <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">
          {getUserInitials()}
        </div>
      </button>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
          {getUserInitials()}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm font-medium text-primary dark:text-white truncate">
            {getUserDisplayName()}
          </div>
          <div className="text-xs text-muted truncate">{user?.email}</div>
        </div>
        <ChevronUpIcon
          className={`w-4 h-4 text-muted transition-transform ${isOpen ? '' : 'rotate-180'}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-700 border border-[rgba(30,77,107,0.15)] dark:border-gray-600 rounded-xl shadow-lg overflow-hidden z-50">
          {/* Theme Toggle */}
          <button
            onClick={toggleDarkMode}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left"
          >
            {isDarkMode ? (
              <SunIcon className="w-5 h-5 text-yellow-500" />
            ) : (
              <MoonIcon className="w-5 h-5 text-primary dark:text-white" />
            )}
            <span className="text-sm text-primary dark:text-white">
              {isDarkMode ? 'Heller Modus' : 'Dunkler Modus'}
            </span>
          </button>

          <div className="border-t border-[rgba(30,77,107,0.1)] dark:border-gray-600" />

          {/* Change Password */}
          <button
            onClick={() => {
              setIsOpen(false);
              setShowPasswordModal(true);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left"
          >
            <KeyIcon className="w-5 h-5 text-primary dark:text-white" />
            <span className="text-sm text-primary dark:text-white">Passwort ändern</span>
          </button>

          {/* Fehler melden */}
          <button
            onClick={() => {
              setIsOpen(false);
              setShowFeedbackDialog(true);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left"
          >
            <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-primary dark:text-white" />
            <span className="text-sm text-primary dark:text-white">Fehler melden</span>
          </button>

          <div className="border-t border-[rgba(30,77,107,0.1)] dark:border-gray-600" />

          {/* Delete Data */}
          <button
            onClick={() => {
              setIsOpen(false);
              setShowDeleteModal(true);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-left"
          >
            <TrashIcon className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-600 dark:text-red-400">Daten löschen</span>
          </button>

          <div className="border-t border-[rgba(30,77,107,0.1)] dark:border-gray-600" />

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-warm-100 dark:hover:bg-gray-600 transition-colors text-left"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-primary dark:text-white" />
            <span className="text-sm text-primary dark:text-white">Abmelden</span>
          </button>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {/* Delete Data Modal */}
      {showDeleteModal && (
        <DeleteDataModal onClose={() => setShowDeleteModal(false)} />
      )}

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
      />
    </div>
  );
};

// Change Password Modal
const ChangePasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    try {
      setLoading(true);
      await apiClient.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setSuccess(true);
      setTimeout(() => onClose(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-primary dark:text-white mb-4">
          Passwort ändern
        </h2>

        {success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-primary dark:text-white">Passwort erfolgreich geändert!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary dark:text-white mb-1">
                Aktuelles Passwort
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary dark:text-white mb-1">
                Neues Passwort
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary dark:text-white mb-1">
                Passwort bestätigen
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Wird geändert...' : 'Ändern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Delete Data Modal (DSGVO Compliant)
const DeleteDataModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { logout } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'LÖSCHEN') {
      setError('Bitte geben Sie "LÖSCHEN" ein, um zu bestätigen.');
      return;
    }

    if (!password) {
      setError('Bitte geben Sie Ihr Passwort ein.');
      return;
    }

    try {
      setLoading(true);
      await apiClient.post('/api/auth/delete-account', { password });
      await logout();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen der Daten');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-red-600">Daten löschen</h2>
        </div>

        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <p className="text-sm text-red-700 dark:text-red-400 mb-2">
            <strong>Achtung:</strong> Diese Aktion ist unwiderruflich!
          </p>
          <p className="text-sm text-red-600 dark:text-red-400">
            Gemäß DSGVO Art. 17 werden folgende Daten gelöscht:
          </p>
          <ul className="text-sm text-red-600 dark:text-red-400 list-disc list-inside mt-2">
            <li>Alle Ihre Chat-Verläufe</li>
            <li>Alle gespeicherten Nachrichten</li>
            <li>Ihre Nutzungsdaten</li>
            <li>Ihr Benutzerkonto</li>
          </ul>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary dark:text-white mb-1">
              Passwort zur Bestätigung
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ihr Passwort"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary dark:text-white mb-1">
              Geben Sie "LÖSCHEN" ein
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
              placeholder="LÖSCHEN"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || confirmText !== 'LÖSCHEN'}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Wird gelöscht...' : 'Endgültig löschen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserMenu;
